import { useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api-client";
import type { StudyGoal } from "@examora/shared/plan/daily-plan";
import type { StudyProfile } from "@examora/shared/plan/study-profile";
import { PLAN_KEY } from "./use-plan";

/**
 * The "start" questionnaire filled in before sign-up waits in this browser, then is saved to the account
 * right after signing in, whichever page that lands on (/plan, /pricing, the dashboard): StudentChrome applies it.
 */
const KEY = "exanabi-start-answers";

export type StartAnswers = { goal: StudyGoal; profile: StudyProfile };

export function savePendingStudyProfile(answers: StartAnswers) {
  try {
    localStorage.setItem(KEY, JSON.stringify({ ...answers, savedAt: Date.now() }));
  } catch {
    // Private mode or blocked storage: the plan just won't carry over; the student can fill it in again.
  }
}

function readPending(): StartAnswers | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StartAnswers & { savedAt?: number };
    // Answers older than a month are stale (the exam date has likely moved on).
    if (!parsed.goal || !parsed.profile || (parsed.savedAt && Date.now() - parsed.savedAt > 30 * 86_400_000)) {
      localStorage.removeItem(KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

/** Whether answers from the questionnaire are waiting to be saved (then the dashboard must not send the student back to it). */
export function hasPendingStudyProfile() {
  return readPending() !== null;
}

export function saveStudyProfile(answers: StartAnswers) {
  return api.put("/api/v1/plan/profile", answers);
}

/** Signed-in pages: saves answers left by the questionnaire, once, and refreshes the plan. */
export function useApplyPendingStudyProfile(signedIn: boolean) {
  const queryClient = useQueryClient();
  const started = useRef(false);
  useEffect(() => {
    if (!signedIn || started.current) return;
    started.current = true;
    const pending = readPending();
    if (!pending) return;
    void saveStudyProfile(pending)
      .then(() => {
        try {
          localStorage.removeItem(KEY);
        } catch {
          // ignore
        }
        return queryClient.invalidateQueries({ queryKey: PLAN_KEY });
      })
      .catch(() => {
        // Left in place: the next signed-in page tries again.
      });
  }, [queryClient, signedIn]);
}
